// Pieces the review box and the side panel both show: the level chip, the AI
// mark, the filter tabs, what has been checked, and the English vs Indonesian
// comparison (its call-out in the box, its line in the panel).

import React, { useEffect, useRef, useState } from 'react';
import { LEVELS, SOURCES, matches } from './reviewModel';

export const lc = (s) => String(s || '').toLowerCase();

export const SevChip = ({ sev }) => <span className={`lr-sev ${lc(sev)}`}>{sev}</span>;

// Its own column, blank for machine findings, so the text after it never moves.
export const AiMark = ({ f, w }) => (f.src === 'ai'
  ? <span className="lr-ai-mark" title={w.aiTip} aria-label={w.aiTip}>✦</span>
  : <span className="lr-ai-mark" aria-hidden="true" />);

export const NewTag = ({ on, w }) => (on ? <span className="lr-new">{w.newTag}</span> : null);

export const ARROWS = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <path d="M4 8h15l-4-4M20 16H5l4 4" />
  </svg>
);

// Semua | HIGH MED LOW | MESIN ✦ AI. Level and source combine; each number
// follows the other filter (with ✦ AI picked, HIGH counts the AI's HIGH ones).
export function Tabs({ findings, level, source, w, onPick }) {
  const base = findings.filter((f) => !f.minor);
  const keys = ['all', '|', ...LEVELS, '|', ...SOURCES];
  return (
    <div className="lr-tabs" role="group">
      {keys.map((k, i) => {
        if (k === '|') return <span key={`sep${i}`} className="lr-sep" aria-hidden="true" />;
        const isLevel = LEVELS.includes(k);
        const label = k === 'all' ? w.all : isLevel ? k : w.src[k];
        const n = k === 'all' ? base.length
          : base.filter((f) => (isLevel ? matches(f, k, source) : matches(f, level, k))).length;
        const on = k === 'all' ? level === 'all' && source === 'all' : isLevel ? level === k : source === k;
        return (
          <button key={k} type="button" className={`lr-pill ${lc(k)}`} aria-pressed={on} onClick={() => onPick(k)}>
            {label}<span className="n">{n}</span>
          </button>
        );
      })}
    </div>
  );
}

// Seconds since a stage was first seen running here. Ticks once a second.
export function useElapsed(active) {
  const start = useRef(null);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!active) { start.current = null; return undefined; }
    if (start.current == null) start.current = Date.now();
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
  return active && start.current ? Math.floor((Date.now() - start.current) / 1000) : 0;
}

const AI_BUSY = ['pending', 'queued', 'running'];
const FAILED = ['unavailable', 'failed', 'degenerate'];
// Measured on the SPA, 2026-10-12: 73 clause pairs in 2 min 45 s through the tailnet.
const SEC_PER_PAIR = 2.5;

// The comparison's state, from the check: idle | queued | running | done |
// failed | none (one language: nothing to compare) | off.
export function cmpState(data) {
  const tr = (data.translation && data.translation.status) || 'not_requested';
  const pairs = data.rules ? data.rules.pairs : null;
  if (pairs === 0 || tr === 'skipped') return 'none';
  if (tr === 'off') return 'off';
  if (tr === 'queued') return data.translation.progress ? 'running' : 'queued';
  if (tr === 'running') return 'running';
  if (tr === 'done') return 'done';
  if (FAILED.includes(tr)) return 'failed';
  return 'idle';
}

export function cmpNumbers(data) {
  const p = (data.translation && data.translation.progress) || null;
  const total = (p && p.total) || data.rules.pairs || 0;
  const done = (p && p.done) || 0;
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0, known: !!(p && p.total) };
}

const estimate = (data) => ((data.rules && data.rules.pairs) || 45) * SEC_PER_PAIR;

// ✓ Mesin  ✓ AI  ○ Inggris vs Indonesia · belum
export function Steps({ data, w, onSee, freshCount }) {
  const ai = data.ai.status;
  const aiBusy = AI_BUSY.includes(ai);
  const sec = useElapsed(ai === 'running');
  const cmp = cmpState(data);
  const { done, total } = cmpNumbers(data);
  let aiPart;
  if (ai === 'done') aiPart = <span className="lr-st ok"><span className="i">✓</span>{w.steps.ai}</span>;
  else if (aiBusy) {
    aiPart = (
      <span className="lr-st run"><span className="lr-spin" aria-hidden="true" />
        {w.steps.ai} · {ai === 'running' ? w.aiWait.running(sec) : w.aiWait[ai]}</span>
    );
  } else {
    aiPart = (
      <span className="lr-st fail" title={w.aiStopped[ai] || ''}><span className="i">!</span>
        {w.steps.ai} · {ai === 'off' ? w.stOff : w.aiStoppedShort}</span>
    );
  }
  const trCount = (data.translation.findings || []).length;
  let cmpPart;
  if (cmp === 'none') cmpPart = <span className="lr-st idle"><span className="i">–</span>{w.steps.cmp} · {w.stNotNeeded}</span>;
  else if (cmp === 'off') cmpPart = <span className="lr-st idle"><span className="i">–</span>{w.steps.cmp} · {w.stOff}</span>;
  else if (cmp === 'running' || cmp === 'queued') {
    cmpPart = (
      <span className="lr-st run"><span className="lr-spin" aria-hidden="true" />
        {w.steps.cmp} {total ? `${done}/${total}` : ''}</span>
    );
  } else if (cmp === 'done') {
    cmpPart = (
      <span className="lr-st ok"><span className="i">✓</span>{w.steps.cmp} · {w.stDone(trCount)}
        {freshCount > 0 && <button type="button" className="lr-link" onClick={onSee}>{w.see}</button>}</span>
    );
  } else if (cmp === 'failed') cmpPart = <span className="lr-st fail"><span className="i">!</span>{w.steps.cmp} · {w.stFail}</span>;
  else cmpPart = <span className="lr-st idle"><span className="i">○</span>{w.steps.cmp} · {w.stIdle}</span>;
  return (
    <div className="lr-steps">
      <span className="lr-st ok"><span className="i">✓</span>{w.steps.rules}</span>
      {aiPart}
      {cmpPart}
    </div>
  );
}

// The call-out in the box: big while there is something to do, gone when done.
export function CmpCallout({ data, w, onStart }) {
  const cmp = cmpState(data);
  const { done, total, pct, known } = cmpNumbers(data);
  if (['done', 'none', 'off'].includes(cmp)) return null;
  if (cmp === 'running' || cmp === 'queued') {
    const left = Math.max(0, total - done) * SEC_PER_PAIR;
    return (
      <div className="lr-cmp" role="status" aria-live="polite">
        <span className="lr-cmp-ic">{ARROWS}</span>
        <div className="lr-cmp-t">
          <b>{cmp === 'queued' ? w.cmpQueuedTitle : w.cmpRunTitle}</b>
          <span>{cmp === 'queued' || !known ? w.cmpQueuedText : w.cmpRunText(done, total, w.mins(left))}</span>
        </div>
        <span />
        <div className={`lr-bar${known ? '' : ' indet'}`}><i style={{ width: `${pct}%` }} /></div>
      </div>
    );
  }
  if (cmp === 'failed') {
    return (
      <div className="lr-cmp fail">
        <span className="lr-cmp-ic">!</span>
        <div className="lr-cmp-t"><b>{w.cmpFailTitle}</b><span>{w.cmpFailText}</span></div>
        <button className="lr-btn primary" type="button" onClick={onStart}>{w.cmpRetry}</button>
      </div>
    );
  }
  return (
    <div className="lr-cmp">
      <span className="lr-cmp-ic">{ARROWS}</span>
      <div className="lr-cmp-t"><b>{w.cmpTitle}</b><span>{w.cmpText(data.rules.pairs, w.mins(estimate(data)))}</span></div>
      <button className="lr-btn primary" type="button" onClick={onStart}>{w.cmpBtn}</button>
    </div>
  );
}

// The same, as one line at the top of the panel's list.
export function CmpStrip({ data, w, onStart }) {
  const cmp = cmpState(data);
  const { done, total, pct, known } = cmpNumbers(data);
  if (['done', 'none', 'off'].includes(cmp)) return null;
  if (cmp === 'running' || cmp === 'queued') {
    return (
      <div className="lr-strip" role="status">
        <span className="lr-spin" aria-hidden="true" />
        <span>{cmp === 'queued' || !known ? <b>{w.stripQueued}</b> : <b>{w.stripRun(done, total)}</b>}</span>
        <div className={`lr-bar${known ? '' : ' indet'}`}><i style={{ width: `${pct}%` }} /></div>
      </div>
    );
  }
  if (cmp === 'failed') {
    return (
      <div className="lr-strip fail">
        <span><b>{w.stripFail}</b></span><span className="lr-grow" />
        <button className="lr-btn primary" type="button" onClick={onStart}>{w.cmpRetry}</button>
      </div>
    );
  }
  return (
    <div className="lr-strip">
      {ARROWS}<span><b>{w.stripIdle(w.mins(estimate(data)))}</b></span><span className="lr-grow" />
      <button className="lr-btn primary" type="button" onClick={onStart}>{w.cmpBtn}</button>
    </div>
  );
}

export const DOCX_ICON = (ext) => (
  <svg width="30" height="36" viewBox="0 0 34 40" aria-hidden="true">
    <path d="M3 1h20l10 10v26a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2z" fill="#e6f0fa" stroke="#9fbcd6" />
    <path d="M23 1v8a2 2 0 0 0 2 2h8" fill="none" stroke="#9fbcd6" />
    <text x="17" y="30" textAnchor="middle" fontSize="9" fontWeight="700" fill="#2b579a" fontFamily="Segoe UI, Arial">{ext}</text>
  </svg>
);
